import type { FC, ReactNode } from 'react';
import { FileText, CheckCircle2, PenLine, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useWebPageVersionsStats } from '@/lib/api/hooks/web-pages';
import { Loader } from '@/components/ui';

interface StatsCardProps {
  title: string;
  value: string | number;
  description: string;
  icon: ReactNode;
  iconContainerClassName: string;
  iconClassName: string;
}

type StatKey = 'totalVersions' | 'publishedCount' | 'draftCount' | 'currentPublishedUpdatedAt';

interface StatConfig {
  key: StatKey;
  title: string;
  icon: LucideIcon;
  iconContainerClassName: string;
  iconClassName: string;
  getValue: (stats: Record<StatKey, string | number | null>) => string | number;
  getDescription: (stats: Record<StatKey, string | number | null>) => string;
}

const StatCard = ({
  title,
  value,
  description,
  icon,
  iconContainerClassName,
  iconClassName,
}: StatsCardProps) => (
  <article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
    <div className="grid grid-cols-[auto_1fr]  gap-4">
      <div className={`flex h-10 w-10 2xl:w-12 2xl:h-12 items-center justify-center rounded-lg ${iconContainerClassName}`}>
        <span className={iconClassName}>{icon}</span>
      </div>
      <div className='flex gap-1 flex-col'>
        <p className="text-sm mb-1 font-medium text-gray-600">{title}</p>
        <p className={`leading-[1.1] font-semibold text-gray-900 ${title.toLowerCase() === "last updated" ? "text-[1.125rem]" : "text-[2rem]"}`}>{value}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  </article>
);

const STATS_CONFIG: StatConfig[] = [
  {
    key: 'totalVersions',
    title: 'Total Versions',
    icon: FileText,
    iconContainerClassName: 'bg-[#E8F6EE70]',
    iconClassName: 'text-[#0F8A47]',
    getValue: (stats) => stats.totalVersions ?? 0,
    getDescription: () => 'All time versions',
  },
  {
    key: 'publishedCount',
    title: 'Published',
    icon: CheckCircle2,
    iconContainerClassName: 'bg-[#E8F6EE70]',
    iconClassName: 'text-[#0F8A47]',
    getValue: (stats) => stats.publishedCount ?? 0,
    getDescription: () => 'Live on website',
  },
  {
    key: 'draftCount',
    title: 'Drafts',
    icon: PenLine,
    iconContainerClassName: 'bg-[#FDF3E670]',
    iconClassName: 'text-[#B86A00]',
    getValue: (stats) => stats.draftCount ?? 0,
    getDescription: () => 'Work in progress',
  },
  {
    key: 'currentPublishedUpdatedAt',
    title: 'Last Updated',
    icon: Clock,
    iconContainerClassName: 'bg-[#F3E8FF70]',
    iconClassName: 'text-[#8B5CF6]',
    getValue: (stats) => {
      if (!stats.currentPublishedUpdatedAt) return '-';
      const date = new Date(stats.currentPublishedUpdatedAt as string);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    getDescription: (stats) => {
      if (!stats.currentPublishedUpdatedAt) return 'No published version';
      const date = new Date(stats.currentPublishedUpdatedAt as string);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
    },
  },
];

interface WebPageVersionsStatsProps {
  pageId: string;
}

const WebPageVersionsStats: FC<WebPageVersionsStatsProps> = ({ pageId }) => {
  const { data: stats, isFetching, refetch, isError } = useWebPageVersionsStats(pageId);

  const values: Record<StatKey, string | number | null> = {
    totalVersions: stats?.totalVersions ?? 0,
    publishedCount: stats?.publishedCount ?? 0,
    draftCount: stats?.draftCount ?? 0,
    currentPublishedUpdatedAt: stats?.currentPublishedUpdatedAt ?? null,
  };

  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {isError ? (
        <div className="col-span-full p-4 flex flex-col items-center justify-center gap-4">
          <p>Error loading statistics.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
            Retry
          </button>
        </div>
      ) : isFetching ? (
        STATS_CONFIG.map((stat) => (
          <div key={stat.key} className="p-4 flex items-center justify-center min-h-28 w-full">
            <Loader size="md" variant="wine" />
          </div>
        ))
      ) : (
        STATS_CONFIG.map((stat) => {
          const Icon = stat.icon;
          return (
            <StatCard
              key={stat.key}
              title={stat.title}
              value={stat.getValue(values)}
              description={stat.getDescription(values)}
              icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
              iconContainerClassName={stat.iconContainerClassName}
              iconClassName={stat.iconClassName}
            />
          );
        })
      )}
    </section>
  );
};

export default WebPageVersionsStats;
